import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type { PerWordResult, VerseMeta } from "../core/types";
import { wordColor } from "../core/palette";
import type { SelectedHit } from "./HeatMap";

export interface OriginalTextProps {
  text: string;
  posmap: Int32Array | null;
  verses: VerseMeta[];
  results: PerWordResult[];
  selected: SelectedHit | null;
  // Cap the number of distinct hits we draw boxes for, per word. Sorted
  // ascending by |d|, so the user always sees the "tightest" hits first.
  maxRenderedHitsPerWord?: number;
  // Word indices that should have their highlights hidden.
  mutedWords?: ReadonlySet<number>;
}

const MIN_TEXT_FONT = 14;
const MAX_TEXT_FONT = 36;
const DEFAULT_TEXT_FONT = 22;

interface VerseHighlight {
  // Offset in the original text (relative to the verse start) of a highlighted
  // letter.
  offsetInVerse: number;
  // Word index this highlight belongs to (for color).
  wordIndex: number;
  // Hit index within that word (for selection).
  hitIndex: number;
}

const EMPTY_HIGHLIGHTS: ReadonlyArray<VerseHighlight> = Object.freeze([]);

const DEFAULT_MAX_RENDERED_HITS_PER_WORD = 200;

interface VerseRange {
  meta: VerseMeta;
  start: number;
  end: number;
}

function buildVerseRanges(verses: VerseMeta[], textLen: number): VerseRange[] {
  const out: VerseRange[] = new Array(verses.length);
  for (let i = 0; i < verses.length; i++) {
    const start = verses[i]!.originalStart;
    const end = i + 1 < verses.length ? verses[i + 1]!.originalStart : textLen;
    out[i] = { meta: verses[i]!, start, end };
  }
  return out;
}

function verseIndexForOffset(verses: VerseMeta[], offset: number): number {
  if (verses.length === 0) return -1;
  let lo = 0;
  let hi = verses.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >>> 1;
    if (verses[mid]!.originalStart <= offset) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

interface PerVerseHighlights {
  byVerse: ReadonlyArray<ReadonlyArray<VerseHighlight>>;
  // Verse index containing the selected hit's first letter, or -1.
  selectedVerseIndex: number;
}

function bucketHighlights(
  results: PerWordResult[],
  posmap: Int32Array,
  verses: VerseMeta[],
  selected: SelectedHit | null,
  maxPerWord: number,
  muted?: ReadonlySet<number>,
): PerVerseHighlights {
  const byVerse: VerseHighlight[][] = new Array(verses.length);
  for (let i = 0; i < verses.length; i++) byVerse[i] = [];

  let selectedVerseIndex = -1;

  for (let wi = 0; wi < results.length; wi++) {
    if (muted && muted.has(wi)) continue;
    const word = results[wi]!;
    // Pick the tightest |d| per word, plus the selected hit if it falls
    // outside that bucket.
    const ordering = word.hits.map((_, i) => i);
    ordering.sort(
      (a, b) => Math.abs(word.hits[a]!.d) - Math.abs(word.hits[b]!.d),
    );
    const renderedSet = new Set(ordering.slice(0, maxPerWord));
    if (selected && selected.wordIndex === wi) {
      renderedSet.add(selected.hitIndex);
    }

    for (const hi of renderedSet) {
      const hit = word.hits[hi];
      if (!hit) continue;
      for (let k = 0; k < hit.len; k++) {
        const compactIdx = hit.p + k * hit.d;
        if (compactIdx < 0 || compactIdx >= posmap.length) continue;
        const orig = posmap[compactIdx]!;
        const vi = verseIndexForOffset(verses, orig);
        if (vi < 0) continue;
        const startOfVerse = verses[vi]!.originalStart;
        byVerse[vi]!.push({
          offsetInVerse: orig - startOfVerse,
          wordIndex: wi,
          hitIndex: hi,
        });
        if (
          selected &&
          selected.wordIndex === wi &&
          selected.hitIndex === hi &&
          k === 0
        ) {
          selectedVerseIndex = vi;
        }
      }
    }
  }

  const finalized: ReadonlyArray<ReadonlyArray<VerseHighlight>> = byVerse.map(
    (arr) => (arr.length === 0 ? EMPTY_HIGHLIGHTS : arr),
  );

  return { byVerse: finalized, selectedVerseIndex };
}

interface VerseProps {
  verseIndex: number;
  verseText: string;
  citation: string;
  highlights: ReadonlyArray<VerseHighlight>;
  selectedKey: string | null;
}

const ESTIMATED_VERSE_HEIGHT = 56;

function selectedKeyOf(sel: SelectedHit | null): string | null {
  if (!sel) return null;
  return `${sel.wordIndex}:${sel.hitIndex}`;
}

const Verse = memo(function Verse({
  verseIndex,
  verseText,
  citation,
  highlights,
  selectedKey,
}: VerseProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hasBeenInView, setHasBeenInView] = useState(false);

  useEffect(() => {
    if (hasBeenInView) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setHasBeenInView(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: "1000px 0px 1000px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasBeenInView]);

  const shouldRender = hasBeenInView || highlights.length > 0;

  let body: React.ReactNode;
  if (!shouldRender) {
    body = null;
  } else if (highlights.length === 0) {
    body = verseText;
  } else {
    // Multiple words can land on the same letter; the most recent wins
    // visually but we draw all colors as a stacked outline.
    const byOffset = new Map<number, VerseHighlight[]>();
    for (const h of highlights) {
      const existing = byOffset.get(h.offsetInVerse);
      if (existing) existing.push(h);
      else byOffset.set(h.offsetInVerse, [h]);
    }
    const segments: React.ReactNode[] = [];
    for (let i = 0; i < verseText.length; i++) {
      const overlays = byOffset.get(i);
      if (overlays) {
        const isSelected = overlays.some(
          (h) => `${h.wordIndex}:${h.hitIndex}` === selectedKey,
        );
        const primary = overlays[0]!;
        const color = wordColor(primary.wordIndex);
        segments.push(
          <span
            key={i}
            className={`hit-letter${isSelected ? " selected" : ""}`}
            style={{
              boxShadow: `0 0 0 ${isSelected ? 3 : 2}px ${color}`,
              color,
            }}
          >
            {verseText[i]}
          </span>,
        );
      } else {
        segments.push(verseText[i]);
      }
    }
    body = segments;
  }

  return (
    <div
      ref={ref}
      className="verse"
      data-verse-index={verseIndex}
      style={{ minHeight: ESTIMATED_VERSE_HEIGHT }}
    >
      <span className="verse-citation">{citation}</span>
      <span className="verse-text">{body}</span>
    </div>
  );
});

export function OriginalText(props: OriginalTextProps): JSX.Element {
  const {
    text,
    posmap,
    verses,
    results,
    selected,
    maxRenderedHitsPerWord = DEFAULT_MAX_RENDERED_HITS_PER_WORD,
    mutedWords,
  } = props;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fontSize, setFontSize] = useState<number>(DEFAULT_TEXT_FONT);

  const zoomIn = () =>
    setFontSize((s) => Math.min(MAX_TEXT_FONT, s + 2));
  const zoomOut = () =>
    setFontSize((s) => Math.max(MIN_TEXT_FONT, s - 2));
  const zoomReset = () => setFontSize(DEFAULT_TEXT_FONT);

  const verseRanges = useMemo(
    () => buildVerseRanges(verses, text.length),
    [verses, text.length],
  );

  const verseTexts = useMemo(() => {
    const out: string[] = new Array(verseRanges.length);
    for (let i = 0; i < verseRanges.length; i++) {
      const r = verseRanges[i]!;
      out[i] = text.slice(r.start, r.end);
    }
    return out;
  }, [text, verseRanges]);

  const citations = useMemo(() => {
    return verseRanges.map(
      (r) => `${r.meta.book} ${r.meta.chapter}:${r.meta.verse}`,
    );
  }, [verseRanges]);

  const bucketed = useMemo(() => {
    if (!posmap) {
      return {
        byVerse: verses.map(() => EMPTY_HIGHLIGHTS),
        selectedVerseIndex: -1,
      } as PerVerseHighlights;
    }
    return bucketHighlights(
      results,
      posmap,
      verses,
      selected,
      maxRenderedHitsPerWord,
      mutedWords,
    );
  }, [results, posmap, verses, selected, maxRenderedHitsPerWord, mutedWords]);

  useEffect(() => {
    if (bucketed.selectedVerseIndex < 0) return;
    const el = containerRef.current?.querySelector<HTMLElement>(
      `[data-verse-index="${bucketed.selectedVerseIndex}"]`,
    );
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [bucketed.selectedVerseIndex]);

  const selectedKey = selectedKeyOf(selected);

  if (!posmap) {
    return <div className="original-text loading">Loading text…</div>;
  }

  return (
    <>
      <div className="matrix-toolbar">
        <span>{verseRanges.length.toLocaleString()} verses</span>
        <span className="toolbar-zoom">
          <button onClick={zoomOut} aria-label="Zoom out">−</button>
          <span className="zoom-readout">{fontSize}px</span>
          <button onClick={zoomIn} aria-label="Zoom in">+</button>
          <button onClick={zoomReset} aria-label="Reset zoom">reset</button>
        </span>
      </div>
      <div
        className="original-text"
        ref={containerRef}
        dir="rtl"
        lang="he"
        style={{ "--text-zoom": `${fontSize}px` } as CSSProperties}
      >
        {verseRanges.map((_, vi) => (
          <Verse
            key={vi}
            verseIndex={vi}
            verseText={verseTexts[vi]!}
            citation={citations[vi]!}
            highlights={bucketed.byVerse[vi]!}
            selectedKey={selectedKey}
          />
        ))}
      </div>
    </>
  );
}
